if (!customElements.get("silxine-cart-gift-box")) {
  class SilxineCartGiftBox extends HTMLElement {
    static busy = false;

    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener("change", this.onChange);
      this.addEventListener("click", this.onClick);
      this.restoreControls();
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.onChange);
      this.removeEventListener("click", this.onClick);
    }

    get quantity() { return Number(this.dataset.quantity) || 0; }

    onChange(event) {
      if (event.target.matches("[data-gift-check]")) {
        this.setQuantity(event.target.checked ? Math.max(1, this.quantity) : 0, "check");
      }
    }

    onClick(event) {
      const button = event.target.closest("[data-gift-action]");
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      const action = button.dataset.giftAction;
      if (action === "refresh") { window.location.reload(); return; }
    }

    restoreControls() {
      const busy = SilxineCartGiftBox.busy;
      const quantity = this.quantity;
      const available = this.dataset.available === "true";
      this.setAttribute("aria-busy", String(busy));
      const check = this.querySelector("[data-gift-check]");
      if (check) { if (!busy) check.checked = quantity > 0; check.disabled = busy || (!available && quantity === 0); }
    }

    showError(message, refresh = false) {
      const error = this.querySelector("[data-gift-error]");
      if (error) { error.textContent = message; error.hidden = !message; }
      const button = this.querySelector('[data-gift-action="refresh"]');
      if (button) button.hidden = !refresh;
    }

    async request(path, body) {
      const root = this.dataset.rootUrl || window.Shopify?.routes?.root || "/";
      const url = new URL(root.replace(/\/?$/, "/") + path, window.location.origin);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, {
          credentials: "same-origin",
          signal: controller.signal,
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          ...(body ? { method: "POST", body: JSON.stringify(body) } : {})
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.description || data.message || "Unable to update gift boxes. Please try again.");
        }
        return data;
      } finally { window.clearTimeout(timeout); }
    }

    giftLines(cart) {
      return (cart.items || []).filter(item => String(item.variant_id) === this.dataset.variantId);
    }

    async renderCart(cart, helper, specs) {
      let sections = cart.sections || {};
      if (specs.some(spec => !sections[spec.section])) {
        const url = new URL(window.location.href);
        url.hash = "";
        url.searchParams.set("sections", specs.map(spec => spec.section).join(","));
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) throw new Error("Unable to refresh your bag.");
        sections = { ...sections, ...await response.json() };
      }
      // Do not leave a stale checkout total visible after a successful cart mutation.
      for (const spec of specs.filter(spec => spec.id !== "Cart-Icon-Bubble")) {
        const html = sections[spec.section];
        if (!html) throw new Error("Your bag changed. Please refresh to see the latest total.");
        const parsed = new DOMParser().parseFromString(html, "text/html");
        if (!parsed.querySelector(spec.selector)) throw new Error("Unable to refresh your bag.");
        if (spec.id === "Cart-Drawer" && !parsed.querySelector("[data-cart-drawer-summary]")) {
          throw new Error("Unable to refresh your bag total.");
        }
      }
      helper.renderSections(sections);
      const eventName = window.PUB_SUB_EVENTS?.cartUpdate || "cart-update";
      document.dispatchEvent(new CustomEvent(eventName, { detail: { cart, source: "silxine-gift-box" } }));
    }

    async setQuantity(quantity, focusAction) {
      if (SilxineCartGiftBox.busy) { this.restoreControls(); return; }
      const max = this.dataset.max ? Number(this.dataset.max) : Infinity;
      if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > max) {
        this.restoreControls();
        this.showError(max < Infinity ? `Choose a whole number from 0 to ${max}.` : "Choose a whole number of gift boxes.");
        return;
      }
      if (quantity > this.quantity && this.dataset.available !== "true") {
        this.restoreControls(); this.showError("Gift boxes are currently unavailable."); return;
      }
      if (quantity === this.quantity) { this.restoreControls(); return; }

      const componentId = this.id;
      const locks = [];
      let helper;
      let specs;
      let refreshed = false;
      let errorMessage = "";
      SilxineCartGiftBox.busy = true;
      this.showError("");
      this.restoreControls();
      const status = this.querySelector("[data-gift-status]");
      if (status) status.textContent = "Updating gift boxes…";

      try {
        // Avera may first synchronize the existing pre-order gift notes.
        const cartItemsClass = customElements.get("cart-items");
        if (cartItemsClass?.giftNoteQueue) await cartItemsClass.giftNoteQueue.catch(() => {});
        const drawer = document.getElementById("Cart-Drawer");
        helper = drawer?.querySelector("cart-items") || document.querySelector("cart-items");
        if (!helper?.getSectionsToRender || !helper?.renderSections) {
          throw new Error("Please refresh your bag before choosing gift boxes.");
        }
        if (document.querySelector("cart-items .is-loading, cart-items[inert]")) {
          throw new Error("Your bag is updating. Please wait and try again.");
        }
        specs = helper.getSectionsToRender();
        for (const target of document.querySelectorAll("[data-cart-drawer-content], [data-cart-drawer-summary], [data-cart-page-content]")) {
          locks.push({ target, inert: target.inert });
          target.inert = true;
        }

        let cart = await this.request("cart.js");
        let lines = this.giftLines(cart);
        if (lines.some(item => item.parent_relationship || item.selling_plan_allocation)) {
          throw new Error("A gift box is already linked to a product or payment plan. Update that item in your bag first.");
        }
        const sectionFields = {
          sections: specs.map(spec => spec.section),
          sections_url: window.location.pathname
        };

        // Consolidate only the selected gift-box SKU; never update garment lines.
        while (lines.length > 1) {
          cart = await this.request("cart/change.js", { id: lines[lines.length - 1].key, quantity: 0, ...sectionFields });
          lines = this.giftLines(cart);
        }
        if (lines.length) {
          cart = await this.request("cart/change.js", { id: lines[0].key, quantity, ...sectionFields });
        } else if (quantity > 0) {
          const added = await this.request("cart/add.js", {
            items: [{ id: Number(this.dataset.variantId), quantity }],
            ...sectionFields
          });
          cart = { ...await this.request("cart.js"), sections: added.sections };
        }
        await this.renderCart(cart, helper, specs);
        refreshed = true;
        const actual = this.giftLines(cart).reduce((total, item) => total + item.quantity, 0);
        if (actual !== quantity) errorMessage = "The available gift-box quantity has changed. Your bag shows the current quantity.";
      } catch (error) {
        errorMessage = error.name === "AbortError"
          ? "The update timed out. Please refresh your bag before trying again."
          : error instanceof TypeError ? "Unable to connect. Please check your connection and try again."
          : error.message || "Unable to update gift boxes. Please try again.";
        // A failed/422 response may still have changed the server cart. Read it; never blindly retry a POST.
        if (helper && specs && locks.length) {
          try {
            const cart = await this.request("cart.js");
            await this.renderCart(cart, helper, specs);
            refreshed = true;
          } catch (_) { /* Keep checkout disabled until a refresh if the current total is unknown. */ }
        }
      } finally {
        SilxineCartGiftBox.busy = false;
        for (const lock of locks) {
          if (lock.target.isConnected) lock.target.inert = lock.inert;
        }
        const current = document.getElementById(componentId) || (this.isConnected ? this : null);
        if (current) {
          current.restoreControls();
          const text = current.querySelector("[data-gift-status]");
          if (text) text.textContent = errorMessage ? "" : quantity > 0 ? "Gift-box quantity updated." : "Gift boxes removed.";
          current.showError(errorMessage, !refreshed && locks.length > 0);
          if (!refreshed && locks.length > 0) {
            for (const button of document.querySelectorAll('.cart-drawer__summary-checkout, #Cart-Page [name="checkout"]')) button.disabled = true;
          }
          current.querySelector("[data-gift-check]")?.focus({ preventScroll: true });
        }
      }
    }
  }

  customElements.define("silxine-cart-gift-box", SilxineCartGiftBox);
}
